import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    Grow,
    Fade,
    alpha,
    CircularProgress,
    Alert,
} from '@mui/material'
import { Star, AutoAwesome, Diamond } from '@mui/icons-material'
import {
    MembershipPlan,
    MembershipPlansResponse,
} from '../../types/subscription'
import { membershipApi } from '../../services/api'
import { useNavigate } from 'react-router-dom'
import SignupHeader from '../PatientPages/SignupHeader'

const SubscriptionPublicPage: React.FC = () => {
    const navigate = useNavigate()

    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(
        'monthly'
    )
    const [plans, setPlans] = useState<MembershipPlan[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const [couponInputs, setCouponInputs] = useState<{ [key: string]: string }>({})
    const [discountedAmounts, setDiscountedAmounts] = useState<{ [key: string]: number }>({})
    const [appliedCoupons, setAppliedCoupons] = useState<{ [key: string]: string }>({})
    const [couponLoading, setCouponLoading] = useState<{ [key: string]: boolean }>({})
    const [toast, setToast] = useState<{ open: boolean; message: string; type: 'success' | 'error' }>({
        open: false,
        message: '',
        type: 'success',
    })

    useEffect(() => {
        fetchPlans()
    }, [])

    const fetchPlans = async () => {
        try {
            setLoading(true)
            setError(null)
            const response: MembershipPlansResponse = await membershipApi.getPlans(1, 10)
            setPlans(response.membership || [])
        } catch (err) {
            console.error('Error fetching plans:', err)
            setError('Failed to load subscription plans. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handlePlanSelect = (plan: MembershipPlan) => {
        const monthlyPrice =
            billingCycle === 'monthly' ? plan.monthly_fee : Math.round(plan.anual_fee / 12)
        const finalAmount =
            discountedAmounts[plan._id] !== undefined ? discountedAmounts[plan._id] : monthlyPrice
        const couponApplied = appliedCoupons[plan._id] || ''

        navigate('/provider-create-account', {
            state: {
                selectedPlan: plan,
                billingCycle,
                amountPaid: finalAmount.toString(),
                totalPrice: finalAmount.toString(),
                couponApplied,
            },
        })
    }

    const applyCoupon = async (planId: string, amount: number) => {
        const couponCode = couponInputs[planId]
        if (!couponCode) {
            setToast({ open: true, message: 'Please enter a coupon code', type: 'error' })
            return
        }
        try {
            setCouponLoading(prev => ({ ...prev, [planId]: true }))
            const res = await membershipApi.getCoupon(couponCode, amount)
            setDiscountedAmounts(prev => ({ ...prev, [planId]: res.finalAmount }))
            setAppliedCoupons(prev => ({ ...prev, [planId]: couponCode }))
            setToast({ open: true, message: 'Coupon applied successfully!', type: 'success' })
        } catch (err: any) {
            setToast({ open: true, message: err?.response?.data?.message || 'Invalid coupon code', type: 'error' })
        } finally {
            setCouponLoading(prev => ({ ...prev, [planId]: false }))
        }
    }

    const removeCoupon = (planId: string) => {
        setDiscountedAmounts(prev => { const n = { ...prev }; delete n[planId]; return n })
        setAppliedCoupons(prev => { const n = { ...prev }; delete n[planId]; return n })
        setCouponInputs(prev => ({ ...prev, [planId]: '' }))
    }

    useEffect(() => {
        if (toast.open) {
            const t = setTimeout(() => setToast(p => ({ ...p, open: false })), 3000)
            return () => clearTimeout(t)
        }
    }, [toast.open])

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f3f4f6', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <SignupHeader navigetUrl="/provider-login" />

            {/* Centered white card container */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    py: { xs: 4, md: 6 },
                    px: { xs: 2, sm: 4 },
                }}
            >
                <Box
                    sx={{
                        bgcolor: 'white',
                        borderRadius: 4,
                        boxShadow: '0 25px 60px rgba(0,0,0,0.12)',
                        width: '100%',
                        maxWidth: 900,
                        overflow: 'hidden',
                    }}
                >
                    {/* Dialog-style Header */}
                    <Fade in timeout={500}>
                        <Box
                            sx={{
                                pt: { xs: 4, md: 5 },
                                pb: 2,
                                px: { xs: 3, md: 5 },
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                position: 'relative',
                                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                {/* Icon */}
                                <Box
                                    sx={{
                                        p: 1.5,
                                        borderRadius: '16px',
                                        background: 'linear-gradient(135deg, #2E7DAF 0%, #1e5a8a 100%)',
                                        display: 'inline-flex',
                                        boxShadow: '0 4px 14px rgba(46, 125, 175, 0.4)',
                                        animation: 'pulse 2s infinite',
                                        '@keyframes pulse': {
                                            '0%': { boxShadow: '0 4px 14px rgba(46, 125, 175, 0.39)' },
                                            '50%': { boxShadow: '0 4px 22px rgba(46, 125, 175, 0.65)' },
                                            '100%': { boxShadow: '0 4px 14px rgba(46, 125, 175, 0.39)' },
                                        },
                                    }}
                                >
                                    <AutoAwesome sx={{ color: 'white', fontSize: '2rem' }} />
                                </Box>

                                {/* Title */}
                                <Typography
                                    variant="h3"
                                    fontWeight={700}
                                    sx={{
                                        background: 'linear-gradient(135deg, #1e293b 0%, #2E7DAF 100%)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        fontSize: { xs: '1.6rem', sm: '2rem', md: '2.5rem' },
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    Choose Your Perfect Plan
                                </Typography>
                            </Box>

                            <Typography
                                variant="body1"
                                sx={{ color: '#64748b', fontSize: { xs: '0.9rem', sm: '1rem' }, maxWidth: 500 }}
                            >
                                Choose a plan to get started on our platform and reach new patients.
                            </Typography>
                        </Box>
                    </Fade>

                    {/* Plan Cards */}
                    <Box
                        sx={{
                            pt: 2,
                            px: { xs: 3, md: 5 },
                            pb: { xs: 4, md: 6 },
                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                        }}
                    >
                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <CircularProgress sx={{ color: '#2E7DAF' }} />
                            </Box>
                        )}

                        {error && (
                            <Box sx={{ mb: 3 }}>
                                <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
                            </Box>
                        )}

                        {!loading && !error && (
                            <Grid container spacing={3} justifyContent="center">
                                {plans.map((plan, index) => (
                                    <Grid item xs={12} md={6} key={plan._id}>
                                        <Grow in timeout={600 + index * 200} style={{ transformOrigin: 'center top' }}>
                                            <Card
                                                sx={{
                                                    borderRadius: 4,
                                                    border: '1px solid #e2e8f0',
                                                    background: 'white',
                                                    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: '0 12px 32px rgba(46, 125, 175, 0.14)',
                                                    },
                                                }}
                                            >
                                                <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                                                    {/* Plan Icon & Name */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                                        <Box
                                                            sx={{
                                                                width: 48,
                                                                height: 48,
                                                                borderRadius: '50%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: 'linear-gradient(135deg, #2E7DAF 0%, #1e5a8a 100%)',
                                                                mr: 2,
                                                                boxShadow: '0 4px 12px rgba(46, 125, 175, 0.3)',
                                                            }}
                                                        >
                                                            {plan.name.toLowerCase().includes('starter') ? (
                                                                <Star sx={{ color: 'white', fontSize: '1.4rem' }} />
                                                            ) : (
                                                                <Diamond sx={{ color: 'white', fontSize: '1.4rem' }} />
                                                            )}
                                                        </Box>
                                                        <Typography variant="h5" fontWeight={700} sx={{ color: '#1e293b' }}>
                                                            {plan.name}
                                                        </Typography>
                                                    </Box>

                                                    {/* Price */}
                                                    <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                                                        <Typography
                                                            variant="h2"
                                                            fontWeight={800}
                                                            sx={{
                                                                lineHeight: 1,
                                                                fontSize: '3.25rem',
                                                                background: 'linear-gradient(135deg, #2E7DAF 0%, #1e5a8a 100%)',
                                                                backgroundClip: 'text',
                                                                WebkitBackgroundClip: 'text',
                                                                WebkitTextFillColor: 'transparent',
                                                            }}
                                                        >
                                                            $
                                                            {discountedAmounts[plan._id] !== undefined
                                                                ? discountedAmounts[plan._id]
                                                                : billingCycle === 'monthly'
                                                                    ? plan.monthly_fee
                                                                    : Math.round(plan.anual_fee / 12)}
                                                        </Typography>
                                                        <Typography variant="h6" sx={{ color: '#64748b', ml: 1, fontWeight: 500 }}>
                                                            /month
                                                        </Typography>
                                                    </Box>

                                                    {/* Strikethrough if discounted */}
                                                    {discountedAmounts[plan._id] !== undefined && (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ color: '#94a3b8', textDecoration: 'line-through', fontSize: '0.95rem', mb: 1 }}
                                                        >
                                                            ${billingCycle === 'monthly' ? plan.monthly_fee : Math.round(plan.anual_fee / 12)}/month
                                                        </Typography>
                                                    )}

                                                    {/* Info box */}
                                                    <Box
                                                        sx={{
                                                            bgcolor: alpha('#2E7DAF', 0.05),
                                                            border: `1px solid ${alpha('#2E7DAF', 0.15)}`,
                                                            borderRadius: 3,
                                                            p: 2,
                                                            mb: 2,
                                                            position: 'relative',
                                                            overflow: 'hidden',
                                                            '&::before': {
                                                                content: '""',
                                                                position: 'absolute',
                                                                top: 0, left: 0, right: 0,
                                                                height: '3px',
                                                                background: 'linear-gradient(90deg, #2E7DAF 0%, #1e5a8a 100%)',
                                                            },
                                                        }}
                                                    >
                                                        {billingCycle === 'yearly' && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                                                <Box
                                                                    sx={{
                                                                        width: 22, height: 22, borderRadius: '50%', bgcolor: '#2E7DAF',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        boxShadow: '0 2px 8px rgba(46, 125, 175, 0.3)',
                                                                    }}
                                                                >
                                                                    <Typography sx={{ color: 'white', fontSize: '0.65rem', fontWeight: 700 }}>$</Typography>
                                                                </Box>
                                                                <Typography variant="body2" sx={{ color: '#2E7DAF', fontWeight: 700, fontSize: '0.9rem' }}>
                                                                    ${plan.anual_fee} billed annually
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 500, display: 'block', mb: 0.25 }}>
                                                            {billingCycle === 'monthly' ? plan.month_discount : `${plan.year_discount} year`}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 500, display: 'block' }}>
                                                            {plan.commission}% Per Claim System Fee
                                                        </Typography>
                                                    </Box>

                                                    {/* Billed monthly label */}
                                                    {billingCycle === 'monthly' && discountedAmounts[plan._id] === undefined && (
                                                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem', mb: 2 }}>
                                                            Billed monthly
                                                        </Typography>
                                                    )}

                                                    {/* Billing Toggle */}
                                                    <Box sx={{ bgcolor: '#F3F4F6', borderRadius: 2, p: 0.5, display: 'flex', mb: 2 }}>
                                                        <Button
                                                            onClick={() => { setBillingCycle('monthly'); removeCoupon(plan._id) }}
                                                            sx={{
                                                                flex: 1, py: 1.25, px: 2, borderRadius: 1.5,
                                                                textTransform: 'none', fontWeight: 600, fontSize: '0.875rem',
                                                                bgcolor: billingCycle === 'monthly' ? '#2E7DAF' : 'transparent',
                                                                color: billingCycle === 'monthly' ? 'white' : '#6B7280',
                                                                boxShadow: billingCycle === 'monthly' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                                                '&:hover': { bgcolor: billingCycle === 'monthly' ? '#1e5a8a' : 'rgba(0,0,0,0.03)' },
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                        >
                                                            Pay Monthly
                                                        </Button>
                                                        <Button
                                                            onClick={() => { setBillingCycle('yearly'); removeCoupon(plan._id) }}
                                                            sx={{
                                                                flex: 1, py: 1.25, px: 2, borderRadius: 1.5,
                                                                textTransform: 'none', fontWeight: 600, fontSize: '0.875rem',
                                                                bgcolor: billingCycle === 'yearly' ? '#2E7DAF' : 'transparent',
                                                                color: billingCycle === 'yearly' ? 'white' : '#6B7280',
                                                                boxShadow: billingCycle === 'yearly' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                                                '&:hover': { bgcolor: billingCycle === 'yearly' ? '#1e5a8a' : 'rgba(0,0,0,0.03)' },
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                        >
                                                            Pay Annually
                                                        </Button>
                                                    </Box>

                                                    {/* Coupon Section */}
                                                    {/* <Box sx={{ mb: 3 }}>
                            {discountedAmounts[plan._id] !== undefined ? (
                              <Box>
                                <Typography sx={{ color: '#16a34a', fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>
                                  🎉 Coupon applied! New price: ${discountedAmounts[plan._id]}/month
                                </Typography>
                                <Button
                                  variant="outlined" color="error" size="small"
                                  onClick={() => removeCoupon(plan._id)}
                                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                                >
                                  Remove Coupon
                                </Button>
                              </Box>
                            ) : (
                              <>
                                <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mb: 0.75 }}>
                                  Have a coupon?
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <input
                                    value={couponInputs[plan._id] || ''}
                                    onChange={e => setCouponInputs(prev => ({ ...prev, [plan._id]: e.target.value }))}
                                    placeholder="Enter coupon"
                                    style={{
                                      flex: 1, padding: '10px 12px',
                                      borderRadius: '8px', border: '1px solid #cbd5e1',
                                      outline: 'none', fontSize: '0.9rem', fontFamily: 'inherit',
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    onClick={() => applyCoupon(plan._id, billingCycle === 'monthly' ? plan.monthly_fee : Math.round(plan.anual_fee / 12))}
                                    disabled={couponLoading[plan._id]}
                                    sx={{
                                      borderRadius: 2, textTransform: 'none', fontWeight: 600,
                                      borderColor: '#2E7DAF', color: '#2E7DAF',
                                      '&:hover': { borderColor: '#1e5a8a', bgcolor: alpha('#2E7DAF', 0.05) },
                                    }}
                                  >
                                    {couponLoading[plan._id] ? <CircularProgress size={18} /> : 'Apply'}
                                  </Button>
                                </Box>
                              </>
                            )}
                          </Box> */}

                                                    {/* CTA */}
                                                    <Button
                                                        fullWidth variant="contained" size="large"
                                                        disabled={!plan.isActive || plan.isDeleted}
                                                        onClick={() => handlePlanSelect(plan)}
                                                        sx={{
                                                            py: 1.75, fontSize: '1rem', fontWeight: 700,
                                                            borderRadius: 3, textTransform: 'none',
                                                            background: 'linear-gradient(135deg, #2E7DAF 0%, #1e5a8a 100%)',
                                                            color: 'white',
                                                            boxShadow: '0 4px 16px rgba(46, 125, 175, 0.4)',
                                                            '&:hover': {
                                                                background: 'linear-gradient(135deg, #1e5a8a 0%, #164e63 100%)',
                                                                boxShadow: '0 8px 24px rgba(46, 125, 175, 0.5)',
                                                                transform: 'translateY(-2px)',
                                                            },
                                                            '&:disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
                                                            transition: 'all 0.3s ease',
                                                        }}
                                                    >
                                                        {!plan.isActive || plan.isDeleted ? 'Unavailable' : 'Select Plan'}
                                                    </Button>
                                                </CardContent>
                                            </Card>
                                        </Grow>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Toast */}
            {toast.open && (
                <Alert
                    severity={toast.type}
                    onClose={() => setToast({ ...toast, open: false })}
                    sx={{
                        position: 'fixed', bottom: 24, right: 24, zIndex: 2000,
                        boxShadow: '0 6px 24px rgba(0,0,0,0.15)', borderRadius: 2, minWidth: 280,
                    }}
                >
                    {toast.message}
                </Alert>
            )}
        </Box>
    )
}

export default SubscriptionPublicPage
